import { useEffect, useMemo, useRef } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type VisibilityRealtimeParams = {
  brandId?: string;
  days: number;
  providers?: string[];
  promptId?: string;
  includeActionables?: boolean;
  includeRunResponses?: boolean;
  enabled?: boolean;
  keepAliveAfterFirstEnable?: boolean;
  resubscribeOnEnable?: boolean;
  queryKey: QueryKey;
};

type VisibilitySocketFilters = {
  brandId: string;
  days: "1" | "7" | "14" | "30" | "90";
  providers?: string;
  promptId?: string;
  includeActionables?: boolean;
  includeRunResponses?: boolean;
};

type VisibilitySocketPayload = {
  brandId: string;
  filters?: VisibilitySocketFilters;
};

type VisibilitySocketResponse = VisibilitySocketPayload & Record<string, any>;

const buildFiltersSignature = (filters: VisibilitySocketFilters) =>
  `${filters.brandId}:${filters.days}:${
    filters.providers ?? ""
  }:${filters.promptId ?? ""}:${filters.includeActionables ? "1" : "0"}:${
    filters.includeRunResponses === false ? "0" : "1"
  }`;

const normalizeDays = (days: number): VisibilitySocketFilters["days"] => {
  const allowed = new Set(["1", "7", "14", "30", "90"]);
  const normalized = String(days);
  if (allowed.has(normalized)) {
    return normalized as VisibilitySocketFilters["days"];
  }
  return "7";
};

const normalizeProviders = (providers?: string[]) => {
  if (!providers || !providers.length) {
    return undefined;
  }
  return providers
    .map((provider) => provider.trim())
    .filter(Boolean)
    .map((provider) => provider.toUpperCase())
    .join(",");
};

export const useVisibilityRealtime = ({
  brandId,
  days,
  providers,
  promptId,
  includeActionables = false,
  includeRunResponses,
  enabled = true,
  keepAliveAfterFirstEnable = false,
  resubscribeOnEnable = false,
  queryKey,
}: VisibilityRealtimeParams) => {
  const queryClient = useQueryClient();
  const latestQueryKeyRef = useRef<QueryKey>(queryKey);
  const hasEnabledRef = useRef(false);
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addVisibilitySubscription = useSocketStore(
    (state) => state.addVisibilitySubscription,
  );
  const setVisibilityQueryKey = useSocketStore(
    (state) => state.setVisibilityQueryKey,
  );
  const removeVisibilityQueryKey = useSocketStore(
    (state) => state.removeVisibilityQueryKey,
  );
  const removeVisibilitySubscription = useSocketStore(
    (state) => state.removeVisibilitySubscription,
  );
  const refreshVisibilitySubscription = useSocketStore(
    (state) => state.refreshVisibilitySubscription,
  );

  useEffect(() => {
    latestQueryKeyRef.current = queryKey;
  }, [queryKey]);

  useEffect(() => {
    if (enabled) {
      hasEnabledRef.current = true;
    }
  }, [enabled]);

  const effectiveEnabled =
    enabled || (keepAliveAfterFirstEnable && hasEnabledRef.current);

  const filters = useMemo<VisibilitySocketFilters | null>(() => {
    if (!brandId) return null;
    return {
      brandId,
      days: normalizeDays(days),
      promptId: promptId || undefined,
      providers: normalizeProviders(providers),
      includeActionables: includeActionables || undefined,
      includeRunResponses,
    };
  }, [brandId, days, providers, promptId, includeActionables, includeRunResponses]);

  useEffect(() => {
    if (!effectiveEnabled || !filters) {
      return;
    }
    const signature = buildFiltersSignature(filters);
    const socket = ensureSocket(API_BASE_URL);

    const subscribePayload = {
      brandId: filters.brandId,
      days: filters.days,
      providers: filters.providers,
      promptId: filters.promptId,
      includeActionables: filters.includeActionables,
      includeRunResponses: filters.includeRunResponses,
    };

    const handleResults = (payload: VisibilitySocketResponse) => {
      if (!payload?.filters) return;
      const payloadSignature = buildFiltersSignature(payload.filters);
      if (payloadSignature !== signature) return;
      const { filters: _filters, brandId: _brandId, ...data } = payload;
      const isInvalidationOnly =
        Object.keys(data).length === 1 && data.updatedAt !== undefined;
      if (isInvalidationOnly) {
        void queryClient.invalidateQueries({
          queryKey: latestQueryKeyRef.current,
          refetchType: "active",
        });
        return;
      }
      queryClient.setQueryData(latestQueryKeyRef.current, data);
    };

    socket.on("visibility:results", handleResults);
    setVisibilityQueryKey(signature, latestQueryKeyRef.current);
    addVisibilitySubscription(signature, subscribePayload);
    if (resubscribeOnEnable && enabled) {
      refreshVisibilitySubscription(signature);
    }

    return () => {
      socket.off("visibility:results", handleResults);
      if (!keepAliveAfterFirstEnable) {
        removeVisibilityQueryKey(signature);
        removeVisibilitySubscription(signature);
      }
    };
  }, [
    effectiveEnabled,
    filters,
    queryClient,
    ensureSocket,
    addVisibilitySubscription,
    setVisibilityQueryKey,
    removeVisibilityQueryKey,
    removeVisibilitySubscription,
    refreshVisibilitySubscription,
    keepAliveAfterFirstEnable,
    resubscribeOnEnable,
  ]);
};
